import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useUser } from '@plato/app-core/hooks/use-user';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import {
    FilterLayout,
    FilterLayoutControls,
    FilterLayoutViews,
} from '@plato/ui-legacy/components/filters/filter-layout';
import type { QuickFilter } from '@plato/ui-legacy/components/filters/lib/filter-utils';
import { QuickFilterList } from '@plato/ui-legacy/components/filters/quick-filter-list';
import {
    SavedFilterViewList,
    type SavedFilterView,
} from '@plato/ui-legacy/components/filters/saved-filter-view-list';
import type { AnyFilter, VerticalFilterOption } from '@plato/ui-legacy/components/filters/types/filter-types';
import type { UiFilterVisibility } from '@plato/ui-legacy/components/filters/types/ui-filter-visibility';
import { VerticalAppliedFilters } from '@plato/ui-legacy/components/filters/vertical-applied-filters';
import {
    VerticalFilters,
    type VerticalFiltersProps,
} from '@plato/ui-legacy/components/filters/vertical-filters';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FilterableSubjectKey } from '../../../types/acl-types.ts';
import { api } from '../../../utils/api.ts';
import { useAcl } from '../../acl/use-acl.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DeleteFilterViewDialog } from '../components/delete-filter-view-dialog.tsx';
import { SavedFilterViewsForViewKey } from '../components/saved-filter-views-for-view-key.tsx';

type VerticalFilterTemplateProps<TFilter extends AnyFilter> = {
    aclSubject: FilterableSubjectKey;
    quickFilters?: QuickFilter<TFilter>[];
    viewKey: string;
    onSaveSuccess?: (savedView: { id: string; name: string }) => void;
    onSaveError?: (error: Error) => void;
    onUpdateSuccess?: (updatedView: { id: string; name: string }) => void;
    onUpdateError?: (error: Error) => void;
} & Omit<
    VerticalFiltersProps<TFilter>,
    | 'viewKey'
    | 'canShareFilterViews'
    | 'defaultCurrency'
    | 'onSaveView'
    | 'onUpdateView'
    | 'editingFilterView'
    | 'open'
    | 'setOpen'
>;

export function VerticalFilterTemplate<TFilter extends AnyFilter>({
    aclSubject,
    filters,
    quickFilters,
    defaultFilters,
    appliedFilters,
    onApplyFilters,
    viewKey,
    onSaveSuccess,
    onSaveError,
    onUpdateSuccess,
    onUpdateError,
    ...props
}: VerticalFilterTemplateProps<TFilter>) {
    const acl = useAcl();
    const apiUtils = api.useUtils();
    const user = useUser();
    const { canShareFilterViews, defaultCurrency, enableNewSavedViews } = useAppConfig();
    const { toast } = useToast();
    const { t } = useTranslation();
    const sendEvent = useAnalytics();
    const [editingFilterView, setEditingFilterView] = useState<
        { id: string; name: string; visibility: UiFilterVisibility } | undefined
    >(undefined);
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

    const { data: filterViewsData = [], isLoading: isLoadingFilterViews } =
        api.platoUser.query.filters.getMany.useQuery({ viewKey }, { enabled: !enableNewSavedViews });

    const filterViews: SavedFilterView<TFilter>[] = useMemo(
        () =>
            filterViewsData.map((view) => ({
                id: view.id,
                name: view.name,
                visibility: view.visibility,
                filters: view.filters as TFilter,
                isOwn: view.createdById === user.id,
            })),
        [filterViewsData, user.id],
    );

    const saveFilterViewMutation = api.platoUser.command.filters.create.useMutation({
        onSuccess: (data) => {
            void apiUtils.platoUser.query.filters.getMany.invalidate();
            onApplyFilters(data.filters as TFilter);
            setFilterPopoverOpen(false);

            toast({
                icon: 'success',
                title: t('filter_view_updated_title'),
            });

            onSaveSuccess?.(data);
            sendEvent('filter_view_created', {
                view_id: data.id,
                view_key: viewKey,
                visibility: data.visibility,
            });
        },
        onError: (error) => {
            onSaveError?.(new Error(error.message));
        },
    });

    const updateFilterViewMutation = api.platoUser.command.filters.update.useMutation({
        onSuccess: (data) => {
            void apiUtils.platoUser.query.filters.getMany.invalidate();

            onApplyFilters(data.filters as TFilter);
            setEditingFilterView(undefined);
            setFilterPopoverOpen(false);

            toast({
                icon: 'success',
                title: t('filter_view_updated_title'),
            });

            onUpdateSuccess?.(data);
            sendEvent('filter_view_updated', {
                view_id: data.id,
                view_key: viewKey,
                visibility: data.visibility,
            });
        },
        onError: (error) => {
            onUpdateError?.(new Error(error.message));
        },
    });

    const handleSaveView = useCallback(
        (name: string, visibility: UiFilterVisibility, currentFilters: TFilter) => {
            saveFilterViewMutation.mutate({
                name,
                visibility,
                viewKey,
                filters: currentFilters,
            });
        },
        [saveFilterViewMutation, viewKey],
    );

    const handleUpdateView = useCallback(
        (viewId: string, name: string, visibility: UiFilterVisibility, currentFilters: TFilter) => {
            updateFilterViewMutation.mutate({
                id: viewId,
                name,
                visibility,
                filters: currentFilters,
            });
        },
        [updateFilterViewMutation],
    );

    const handleEditFilterView = useCallback(
        (viewId: string) => {
            const view = filterViews.find((v) => v.id === viewId);
            if (view) {
                setEditingFilterView({
                    id: view.id,
                    name: view.name,
                    visibility: view.visibility,
                });

                onApplyFilters(view.filters);

                setFilterPopoverOpen(true);
            }
        },
        [filterViews, onApplyFilters],
    );

    const handleApplyFilterView = useCallback(
        (filters: TFilter, appliedView?: SavedFilterView<TFilter>) => {
            onApplyFilters(filters);

            if (appliedView) {
                sendEvent('filter_view_applied', {
                    view_id: appliedView.id,
                    view_key: viewKey,
                    visibility: appliedView.visibility,
                });
            }
        },
        [onApplyFilters, sendEvent, viewKey],
    );

    const handleDeleteFilterView = useCallback(
        (viewId: string) => {
            void DialogStateManager.show(DeleteFilterViewDialog, { viewId, viewKey });
        },
        [viewKey],
    );

    const handleFilterPopoverOpenChange = useCallback(
        (value: React.SetStateAction<boolean>) => {
            const newOpen = typeof value === 'function' ? value(filterPopoverOpen) : value;
            setFilterPopoverOpen(newOpen);
            if (!newOpen) {
                setEditingFilterView(undefined);
            }
        },
        [filterPopoverOpen],
    );

    if (!acl.can('filter', `${aclSubject}Filter`)) {
        return null;
    }

    // Filter by ACL permissions
    const allowedFilters = filters.filter(
        (filter): filter is VerticalFilterOption<TFilter> =>
            filter !== undefined && acl.can('filter', `${aclSubject}Filter`, filter.id as never),
    );

    return (
        <FilterLayout>
            <FilterLayoutControls>
                {enableNewSavedViews ? (
                    <>
                        <VerticalFilters
                            defaultFilters={defaultFilters}
                            appliedFilters={appliedFilters}
                            onApplyFilters={onApplyFilters}
                            filters={allowedFilters}
                            defaultCurrency={defaultCurrency}
                            {...props}
                        />
                        <SavedFilterViewsForViewKey<TFilter>
                            viewKey={viewKey}
                            appliedFilters={appliedFilters}
                            defaultFilters={defaultFilters}
                            onApplyFilters={onApplyFilters}
                            quickFilters={quickFilters}
                        />
                    </>
                ) : (
                    <>
                        <VerticalFilters
                            defaultFilters={defaultFilters}
                            appliedFilters={appliedFilters}
                            onApplyFilters={onApplyFilters}
                            filters={allowedFilters}
                            viewKey={viewKey}
                            canShareFilterViews={canShareFilterViews}
                            defaultCurrency={defaultCurrency}
                            onSaveView={handleSaveView}
                            onUpdateView={handleUpdateView}
                            editingFilterView={editingFilterView}
                            open={filterPopoverOpen}
                            setOpen={handleFilterPopoverOpenChange}
                            {...props}
                        />
                        <FilterLayoutViews>
                            {quickFilters && (
                                <QuickFilterList
                                    quickFilters={quickFilters}
                                    currentlyActiveFilters={appliedFilters}
                                    onApplyQuickFilter={onApplyFilters}
                                />
                            )}
                            <SavedFilterViewList
                                views={filterViews}
                                isLoading={isLoadingFilterViews}
                                currentlyActiveFilters={appliedFilters}
                                onApplyFilterView={handleApplyFilterView}
                                onEditFilterView={handleEditFilterView}
                                onDeleteFilterView={handleDeleteFilterView}
                                quickFilters={quickFilters}
                            />
                        </FilterLayoutViews>
                    </>
                )}
            </FilterLayoutControls>

            {allowedFilters.length > 0 && (
                <VerticalAppliedFilters
                    defaultCurrency={defaultCurrency}
                    filters={allowedFilters}
                    defaultFilters={defaultFilters}
                    appliedFilters={appliedFilters}
                    onApplyFilters={onApplyFilters}
                />
            )}
        </FilterLayout>
    );
}
